(() => {
  const root = document.querySelector('[data-dyberry-capsules]');
  if (!root) return;

  const mode = root.dataset.mode;
  const slug = String(root.dataset.capsuleSlug || '').trim();
  const status = root.querySelector('[data-capsules-status]');
  const hub = root.querySelector('[data-capsules-hub]');
  const detail = root.querySelector('[data-capsules-detail]');
  const INITIAL_PRODUCT_BATCH = 12;
  const CAPSULE_MANIFEST_SCHEMA_VERSION = 2;

  const validIndex = (value) => value
    && value.schema_version === CAPSULE_MANIFEST_SCHEMA_VERSION
    && Array.isArray(value.capsules);
  const validDetail = (value, expectedSlug) => value
    && value.schema_version === CAPSULE_MANIFEST_SCHEMA_VERSION
    && value.capsule
    && value.capsule.slug === expectedSlug
    && Array.isArray(value.products);

  // No localStorage fallback: this is a live rack while the UI is being tuned.
  const index = window.DYBERRY_CAPSULES_MANIFEST;
  const capsuleDetail = window.DYBERRY_CAPSULE_MANIFEST;

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  // Manifest URLs are data, never markup. Keep a malformed upstream value from
  // creating a dead link or an executable URL on a live storefront.
  const safeUrl = (value) => {
    if (!value || typeof value !== 'string') return '';
    try {
      const parsed = new URL(value, window.location.origin);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '';
      return value.startsWith('/') ? parsed.pathname + parsed.search + parsed.hash : parsed.href;
    } catch (_) {
      return '';
    }
  };

  const productKey = (product) => String(product && (product.item_id || product.url || product.image_url || product.title) || '');

  const productImage = (product, className, eager = false) => {
    const imageUrl = safeUrl(product && product.image_url);
    if (!imageUrl) {
      const empty = element('div', className + ' dyberry-capsules__image--empty');
      empty.append(element('span', '', 'From the rack'));
      return empty;
    }
    const wrapper = element('span', className);
    const image = document.createElement('img');
    image.alt = product && product.is_editorial_art ? (product.title || 'Capsule editorial art') : cleanProductTitle(product);
    image.decoding = 'async';
    image.loading = eager ? 'eager' : 'lazy';
    if (eager) image.fetchPriority = 'high';
    image.src = imageUrl;
    wrapper.append(image);
    return wrapper;
  };

  const visualCandidates = (capsule) => {
    const candidates = [];
    const add = (piece) => {
      if (!piece || !piece.image_url || candidates.some((entry) => productKey(entry) === productKey(piece))) return;
      candidates.push(piece);
    };
    // The new media contract is explicit: only a human-published image hero can
    // override the legacy editorial header. Drafts and private lab approvals never
    // reach this manifest or this storefront surface.
    const publishedHero = capsule && capsule.editorial_media && capsule.editorial_media.hero;
    const approvedArt = capsule && (
      (publishedHero && publishedHero.asset_type === 'image' && publishedHero.url)
      || capsule.editorial_image_url
      || (capsule.editorial_art && capsule.editorial_art.image_url)
    );
    if (approvedArt) {
      add({
        item_id: 'editorial-' + String(capsule.slug || ''),
        image_url: approvedArt,
        title: (capsule.title || 'Dyberry capsule') + ' editorial art',
        is_editorial_art: true,
      });
    }
    add(capsule && capsule.hero);
    (capsule && Array.isArray(capsule.thumbs) ? capsule.thumbs : []).forEach(add);
    return candidates;
  };

  const chooseVisuals = (capsule, usedKeys = new Set(), count = 2) => {
    const candidates = visualCandidates(capsule);
    const desiredCount = candidates[0] && candidates[0].is_editorial_art ? 1 : count;
    const selected = [];
    candidates.forEach((piece) => {
      if (selected.length < desiredCount && !usedKeys.has(productKey(piece))) selected.push(piece);
    });
    candidates.forEach((piece) => {
      if (selected.length < desiredCount && !selected.some((entry) => productKey(entry) === productKey(piece))) selected.push(piece);
    });
    selected.forEach((piece) => usedKeys.add(productKey(piece)));
    return selected;
  };

  const productCollage = (pieces, modifier, eager = false) => {
    const collage = element('div', 'dyberry-capsules__collage ' + modifier);
    const visuals = pieces && pieces.length ? pieces : [null, null];
    if (visuals[0] && visuals[0].is_editorial_art) {
      collage.classList.add('dyberry-capsules__collage--editorial');
      collage.append(productImage(visuals[0], 'dyberry-capsules__collage-panel', eager));
      return collage;
    }
    visuals.slice(0, 2).forEach((piece) => collage.append(productImage(piece, 'dyberry-capsules__collage-panel', eager)));
    return collage;
  };

  // A published motion asset is optional. Keep its bytes off the critical path:
  // use the approved still as a poster and attach the video source only near view.
  const motionVisual = (capsule, fallback) => {
    const presentation = capsule && capsule.presentation || {};
    const mode = presentation.mode || 'motion_alternate';
    if (mode === 'still_only') return null;
    const motion = capsule && capsule.editorial_media && capsule.editorial_media.motion;
    if (!motion || motion.asset_type !== 'video' || !motion.url) return null;
    const wrapper = element('div', 'dyberry-capsules__motion-wrap');
    const hero = capsule.editorial_media && capsule.editorial_media.hero;
    const stillUrl = safeUrl(hero && hero.url) || safeUrl(motion.poster_url);
    if (stillUrl) {
      const still = element('img', 'dyberry-capsules__motion-still');
      still.src = stillUrl;
      still.alt = (capsule.title || 'Capsule') + ' editorial hero';
      wrapper.append(still);
    }
    const video = element('video', 'dyberry-capsules__motion');
    video.muted = true;
    video.loop = mode === 'motion_loop';
    video.playsInline = true;
    video.preload = 'none';
    video.autoplay = true;
    video.dataset.src = safeUrl(motion.url);
    if (!video.dataset.src) return null;
    video.poster = stillUrl;
    video.setAttribute('aria-label', (capsule.title || 'Capsule') + ' moving editorial treatment');
    video.addEventListener('error', () => {
      video.pause();
      video.removeAttribute('src');
      if (fallback) wrapper.replaceWith(fallback);
    }, { once: true });
    const load = () => {
      if (!video.src) video.src = video.dataset.src;
      video.play().catch(() => {});
    };
    if (mode === 'motion_alternate' && stillUrl) {
      let stillTimer = 0;
      video.addEventListener('ended', () => {
        wrapper.classList.add('is-still');
        window.clearTimeout(stillTimer);
        stillTimer = window.setTimeout(() => {
          video.currentTime = 0;
          video.play().then(() => wrapper.classList.remove('is-still')).catch(() => {});
        }, Math.max(1, Number(presentation.still_hold_seconds) || 10) * 1000);
      });
    }
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) { load(); observer.disconnect(); }
      }, { rootMargin: '240px' });
      observer.observe(video);
    } else load();
    wrapper.append(video);
    wrapper.append(element('p', 'dyberry-capsules__motion-note', 'Moving editorial treatment'));
    return wrapper;
  };

  const displayType = (product) => [product && product.color, product && product.item_type]
    .filter(Boolean)
    .join(' · ') || 'Current rack piece';

  const usableFact = (value) => {
    const fact = String(value || '').trim();
    return fact && fact.toLowerCase() !== 'unknown' ? fact : '';
  };

  const conciseEditorialCopy = (capsule) => {
    const source = usableFact(capsule && capsule.story && capsule.story.full_narrative)
      || usableFact(capsule && capsule.premise);
    if (!source) return '';
    const firstParagraph = source.split(/\n\s*\n/)[0].trim();
    if (firstParagraph.length <= 420) return firstParagraph;
    const sentences = firstParagraph.match(/[^.!?]+[.!?]+/g) || [firstParagraph];
    return sentences.slice(0, 2).join(' ').trim();
  };

  const titleCase = (value) => String(value || '')
    .trim()
    .split(/\s+/)
    .map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : '')
    .join(' ');

  const cleanMaterial = (value) => {
    const material = usableFact(value).toLowerCase();
    if (!material) return '';
    if (material.includes('rayon') && material.includes('wool')) return 'Rayon–wool';
    if (material.includes('polyester') && material.includes('cotton')) return 'Poly–cotton';
    if (material.includes('wool')) return 'Wool';
    if (material.includes('cotton')) return 'Cotton';
    if (material.includes('linen')) return 'Linen';
    if (material.includes('silk')) return 'Silk';
    if (material.includes('leather')) return 'Leather';
    const withoutPercentages = material.replace(/\b\d+(?:\.\d+)?%?\s*/g, '').replace(/[,/]+/g, ' ').trim();
    return withoutPercentages.length <= 24 ? titleCase(withoutPercentages) : '';
  };

  const cleanItemType = (product) => {
    const type = usableFact(product && product.item_type).toLowerCase();
    const sourceTitle = usableFact(product && product.title);
    if (/blazer skirt suit set/i.test(type)) return 'Blazer & Skirt Set';
    if (/\b(emt|ems)\b/i.test(sourceTitle) && /6[ -]?pocket/i.test(sourceTitle)) return 'Six-Pocket EMT Trousers';
    if (type === 'pea coat') return 'Pea Coat';
    if (type === 'pants') return 'Trousers';
    return type ? titleCase(type) : 'Current Piece';
  };

  const patternLabel = (title) => {
    const source = String(title || '');
    const patterns = [
      [/\bplaid\b/i, 'Plaid'], [/\bstripe(?:d|s)?\b/i, 'Striped'], [/\bfloral\b/i, 'Floral'],
      [/\b(?:camo|camouflage)\b/i, 'Camo'], [/\bhoundstooth\b/i, 'Houndstooth'],
    ];
    const match = patterns.find(([expression]) => expression.test(source));
    return match ? match[1] : '';
  };

  const cleanProductTitle = (product) => {
    const pattern = patternLabel(product && product.title);
    const type = cleanItemType(product);
    const material = cleanMaterial(product && product.material);
    const titleMaterial = material === 'Rayon–wool' ? 'Wool-Blend' : material;
    const parts = [pattern, /EMT|Trousers/.test(type) ? '' : titleMaterial, type];
    return parts.filter((part, index) => part && parts.indexOf(part) === index).join(' ') || 'Current Piece';
  };

  const narrativeRole = (role) => ({
    anchor: 'The anchor', texture: 'The texture', contrast: 'The contrast',
    accessory: 'The accent', supporting_cast: 'Supporting cast',
  }[String(role || '').toLowerCase()] || 'In the story');

  const productFacts = (product) => [
    genderLabel(product && product.gender),
    usableFact(product && product.size) ? 'Size ' + usableFact(product.size) : '',
    usableFact(product && product.color),
    cleanMaterial(product && product.material),
  ].filter((fact, index, facts) => fact && facts.indexOf(fact) === index).join(' · ');

  const contributionMap = (capsule) => {
    const map = new Map();
    (capsule && Array.isArray(capsule.item_contributions) ? capsule.item_contributions : [])
      .forEach((item) => map.set(String(item.item_id), item));
    return map;
  };

  const orderedProducts = (products, contributions) => products
    .map((product, index) => ({ product, index, contribution: contributions.get(String(product.item_id)) }))
    .sort((left, right) => {
      const leftRank = Number(left.contribution && left.contribution.rank);
      const rightRank = Number(right.contribution && right.contribution.rank);
      if (Number.isFinite(leftRank) && Number.isFinite(rightRank)) return leftRank - rightRank;
      if (Number.isFinite(leftRank)) return -1;
      if (Number.isFinite(rightRank)) return 1;
      return left.index - right.index;
    })
    .map(({ product }) => product);

  const stableIndex = (product, count) => {
    const key = String((product && (product.item_id || product.url || product.title)) || 'dyberry');
    let hash = 2166136261;
    for (let index = 0; index < key.length; index += 1) {
      hash ^= key.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0) % count;
  };

  const genderLabel = (gender) => ({
    men: 'men’s', women: 'women’s', unisex: 'unisex', boys: 'boys’', girls: 'girls’',
  }[String(gender || '').toLowerCase()] || '');

  const capsulePullNote = (product) => {
    const brand = usableFact(product && product.brand) || 'Dyberry';
    const color = usableFact(product && product.color);
    const type = usableFact(product && product.item_type);
    const material = usableFact(product && product.material);
    const activity = usableFact(product && product.activity);
    const group = usableFact(product && product.item_group);
    const size = usableFact(product && product.size);
    const gender = genderLabel(product && product.gender);
    const price = Number(product && product.price);
    const notes = [];

    if (color && material && type) notes.push(`${brand} in ${color.toLowerCase()} ${material.toLowerCase()} — a ${type.toLowerCase()} from the live pull.`);
    if (brand && color && type) notes.push(`${brand} brings ${color.toLowerCase()} into this ${type.toLowerCase()} story.`);
    if (size && gender && material && type) notes.push(`Marked ${size}; a ${gender} ${material.toLowerCase()} ${type.toLowerCase()} in the current pull.`);
    if (activity && type) notes.push(`A ${type.toLowerCase()} filed in the ${activity.toLowerCase()} lane.`);
    if (group && color) notes.push(`Selected from the ${group.toLowerCase()} side of the rack, in ${color.toLowerCase()}.`);
    if (Number.isFinite(price) && price > 0) notes.push(`Live at $${price.toFixed(2)} — one current thread in the pull.`);
    if (!notes.length) notes.push('A current piece from the live capsule pull.');
    return notes[stableIndex(product, notes.length)];
  };

  const productCard = (product, contribution) => {
    const destination = safeUrl(product && product.url);
    const link = element(destination ? 'a' : 'article', 'dyberry-capsules__product-card');
    if (destination) link.href = destination;
    const frame = element('div', 'dyberry-capsules__product-frame');
    frame.append(productImage(product, 'dyberry-capsules__product-image'));
    const info = element('div', 'dyberry-capsules__product-info');
    if (contribution) {
      const rank = Number(contribution.rank);
      info.append(element(
        'p',
        'dyberry-capsules__product-role',
        (Number.isFinite(rank) ? String(rank).padStart(2, '0') + ' · ' : '') + narrativeRole(contribution.narrative_role)
      ));
    }
    info.append(element('p', 'dyberry-capsules__product-brand', product.brand || 'Dyberry'));
    info.append(element('h3', 'dyberry-capsules__product-title', cleanProductTitle(product)));
    const facts = productFacts(product);
    if (facts) info.append(element('p', 'dyberry-capsules__product-facts', facts));
    frame.append(info);
    link.append(frame);
    link.append(element(
      'p',
      'dyberry-capsules__product-caption',
      usableFact(contribution && contribution.merchandising_line) || capsulePullNote(product)
    ));
    const footer = element('div', 'dyberry-capsules__product-footer');
    const price = Number(product && product.price);
    footer.append(element('span', 'dyberry-capsules__product-price', Number.isFinite(price) && price > 0 ? '$' + price.toFixed(2) : 'View current price'));
    footer.append(element('span', 'dyberry-capsules__product-cta', destination ? 'View piece →' : 'Current piece'));
    link.append(footer);
    return link;
  };

  const capsuleCard = (capsule, featured, visuals) => {
    const destination = safeUrl(capsule && capsule.page_url);
    const link = element(destination ? 'a' : 'article', 'dyberry-capsules__capsule-card' + (featured ? ' dyberry-capsules__hero-card' : ''));
    if (destination) link.href = destination;
    link.append(productCollage(visuals, featured ? 'dyberry-capsules__collage--feature' : 'dyberry-capsules__collage--tile', featured));

    const copy = element('div', 'dyberry-capsules__capsule-copy');
    copy.append(element('p', 'dyberry-capsules__kicker', featured ? 'Featured capsule' : 'Capsule'));
    copy.append(element(featured ? 'h2' : 'h3', '', capsule.title || 'Untitled capsule'));
    if (capsule.headline) copy.append(element('p', 'dyberry-capsules__capsule-headline', capsule.headline));
    const count = Number(capsule.product_count || 0);
    copy.append(element('p', 'dyberry-capsules__capsule-meta', count + ' live piece' + (count === 1 ? '' : 's')));
    link.append(copy);
    return link;
  };

  const refreshedLabel = (generatedAt) => {
    const date = new Date(generatedAt);
    if (Number.isNaN(date.getTime())) return 'Live rack';
    return 'Live rack · refreshed ' + date.toLocaleString([], {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  const showEmpty = (target, message) => {
    target.replaceChildren(element('p', 'dyberry-capsules__empty', message));
    if (status) status.textContent = message;
  };

  const renderHub = (manifest) => {
    if (!hub) return;
    const capsules = manifest.capsules.filter((capsule) => capsule && capsule.slug && capsule.page_url);
    if (!capsules.length) {
      showEmpty(hub, 'Capsules are being refreshed. Please check back in a minute.');
      return;
    }
    const usedVisuals = new Set();
    hub.replaceChildren();
    hub.append(capsuleCard(capsules[0], true, chooseVisuals(capsules[0], usedVisuals)));

    const supporting = capsules.slice(1, 4);
    if (supporting.length) {
      const grid = element('div', 'dyberry-capsules__supporting');
      supporting.forEach((capsule) => grid.append(capsuleCard(capsule, false, chooseVisuals(capsule, usedVisuals))));
      hub.append(grid);
    }

    const remaining = capsules.slice(4);
    if (remaining.length) {
      const more = element('div', 'dyberry-capsules__more');
      more.append(element('p', 'dyberry-capsules__more-heading', 'More worlds from the rack'));
      remaining.forEach((capsule) => more.append(capsuleCard(capsule, false, chooseVisuals(capsule, usedVisuals))));
      hub.append(more);
    }

    if (status) {
      status.textContent = capsules.length + ' live capsules · '
        + Number(manifest.totals.memberships || 0) + ' active threads. '
        + refreshedLabel(manifest.generated_at) + '.';
    }
  };

  const appendProductBatch = (grid, products, start, contributions) => {
    products.slice(start, start + INITIAL_PRODUCT_BATCH)
      .forEach((product) => grid.append(productCard(product, contributions.get(String(product.item_id)))));
    return Math.min(start + INITIAL_PRODUCT_BATCH, products.length);
  };

  const storyMoves = (capsule) => {
    const sections = capsule && capsule.story && Array.isArray(capsule.story.sections) ? capsule.story.sections : [];
    if (!sections.length) return null;
    const wrapper = element('section', 'dyberry-capsules__story');
    const heading = element('header', 'dyberry-capsules__story-heading');
    heading.append(element('p', 'dyberry-capsules__kicker', 'The editorial thread'));
    heading.append(element('h2', '', 'The story in ' + sections.length + ' moves'));
    wrapper.append(heading);
    const grid = element('div', 'dyberry-capsules__story-grid');
    sections.forEach((section, index) => {
      const move = element('article', 'dyberry-capsules__story-move');
      move.append(element('p', 'dyberry-capsules__story-number', String(index + 1).padStart(2, '0')));
      move.append(element('h3', '', section.heading || 'Story move'));
      if (section.body) move.append(element('p', '', section.body));
      grid.append(move);
    });
    wrapper.append(grid);
    return wrapper;
  };

  const renderDetail = (manifest) => {
    if (!detail) return;
    const capsule = manifest.capsule;
    if (!capsule || !Array.isArray(manifest.products) || !manifest.products.length) {
      showEmpty(detail, 'This capsule is being refreshed. Please check back in a minute.');
      return;
    }
    detail.replaceChildren();
    const contributions = contributionMap(capsule);
    const products = orderedProducts(manifest.products, contributions);
    const detailVisuals = chooseVisuals({ ...capsule, thumbs: products.slice(1, 6) }, new Set());
    const fallbackVisual = productCollage(detailVisuals, 'dyberry-capsules__collage--detail', true);
    const motion = motionVisual(capsule, fallbackVisual);
    detail.append(motion || fallbackVisual);

    const header = element('header', 'dyberry-capsules__detail-header');
    const titleBlock = element('div', 'dyberry-capsules__detail-title');
    titleBlock.append(element('p', 'dyberry-capsules__eyebrow', 'Dyberry capsule'));
    titleBlock.append(element('h1', '', capsule.title || 'Capsule'));
    if (capsule.headline) titleBlock.append(element('p', 'dyberry-capsules__capsule-headline', capsule.headline));
    header.append(titleBlock);
    const introBlock = element('div', 'dyberry-capsules__detail-intro');
    const editorialIntro = conciseEditorialCopy(capsule);
    if (editorialIntro) introBlock.append(element('p', 'dyberry-capsules__premise', editorialIntro));
    introBlock.append(element(
      'p',
      'dyberry-capsules__detail-meta',
      Number(capsule.product_count || products.length) + ' live pieces · ' + refreshedLabel(manifest.generated_at)
    ));
    header.append(introBlock);
    detail.append(header);

    const moves = storyMoves(capsule);
    if (moves) detail.append(moves);

    const shopHeader = element('header', 'dyberry-capsules__shop-header');
    const shopTitle = element('div', '');
    shopTitle.append(element('p', 'dyberry-capsules__kicker', 'Shop the story'));
    shopTitle.append(element('h2', '', 'The current pull'));
    shopHeader.append(shopTitle);
    shopHeader.append(element('p', '', 'Every piece below is live now, arranged in the order it enters the story.'));
    detail.append(shopHeader);

    const grid = element('div', 'dyberry-capsules__product-grid');
    if (products.length <= 6) grid.classList.add('dyberry-capsules__product-grid--editorial');
    detail.append(grid);
    let nextProduct = appendProductBatch(grid, products, 0, contributions);
    if (nextProduct < products.length) {
      const sentinel = element('div', 'dyberry-capsules__load-sentinel');
      sentinel.append(element('span', '', 'More from the live pull.'));
      const loadButton = element('button', 'dyberry-capsules__load-more', 'Show 12 more');
      loadButton.type = 'button';
      sentinel.append(loadButton);
      detail.append(sentinel);
      let isLoading = false;
      const loadNext = () => {
        if (isLoading) return false;
        isLoading = true;
        loadButton.disabled = true;
        loadButton.textContent = 'Loading…';
        nextProduct = appendProductBatch(grid, products, nextProduct, contributions);
        if (nextProduct >= products.length) {
          sentinel.remove();
          return true;
        }
        isLoading = false;
        loadButton.disabled = false;
        loadButton.textContent = 'Show 12 more';
        return false;
      };
      // Product batches are operator-visible and deterministic: one activation
      // adds exactly 12. An observer plus a button could race and append 24.
      loadButton.addEventListener('click', loadNext);
    }
    if (status) status.remove();
  };

  if (mode === 'hub') {
    if (validIndex(index)) renderHub(index);
    else if (hub) showEmpty(hub, 'Capsules are temporarily unavailable. Please refresh and try again.');
    return;
  }

  if (mode === 'detail') {
    if (!slug) {
      if (detail) showEmpty(detail, 'This capsule page is missing its capsule handle.');
      return;
    }
    const listedCapsule = validIndex(index) && index.capsules.some((capsule) => capsule.slug === slug);
    if (!listedCapsule) {
      if (detail) showEmpty(detail, 'This capsule is not on the current rack.');
      return;
    }
    if (validDetail(capsuleDetail, slug)) renderDetail(capsuleDetail);
    else if (detail) showEmpty(detail, 'This capsule is temporarily unavailable. Please refresh and try again.');
  }
})();
